//* 组件库
import { $loading, $message } from "various-ui";
//* 公共组件
import type { ConfirmModal } from "@/components/windows";
import type { DiagramsOption } from "@/components/visualizations/diagrams/default";
//* 私有组件
import type { ConsultForm } from "../components";

export const useComposable = () => {
    //* 插件
    const i18n = useI18n();

    //* 静态数据
    const vars = {
        id: "",
        data: [] as { name: string; sequence: string }[],
        timeout: undefined as NodeJS.Timeout | undefined,
        observer: undefined as ResizeObserver | undefined,
        download: "",
        controller: undefined as AbortController | undefined,
        sereotype: [
            {
                label: "AAV2",
                value: "aav2",
                message: i18n.t("AAVMessage2"),
                data: [
                    { name: "sequence 1", sequence: "TTTGATTTGGCGCTTACTAATACG" },
                    { name: "sequence 2", sequence: "CCTGGGACTTCTTTTCTGGTG" },
                    { name: "sequence 3", sequence: "GGTTCGACTAGGTGTCCGGATTCG" },
                    { name: "sequence 4", sequence: "CCTATTCGTGATGGTGGTATGATGATG" },
                    { name: "sequence 5", sequence: "CCTCCTACGTTGGAGCAGCGGCCTTTTGTT" },
                    { name: "sequence 6", sequence: "GCGTCTTGTTGTGTGAATCTGTCG" },
                    { name: "sequence 7", sequence: "CCGACGCGTGTGCTTCTGCAGGCTCAGTTG" },
                    { name: "sequence 8", sequence: "CCGCCTCTTTGTCATGCTGAGATTACT" },
                    { name: "sequence 9", sequence: "CTGCGGCATACGCCTACGATTATT" },
                    { name: "sequence 10", sequence: "ATGGCGCAGCTGTTGCCTGATCTGTTTTGG" },
                    { name: "sequence 11", sequence: "TATTCTAAGTCGTCGTTGGGGAGTCATGCG" },
                    { name: "sequence 12", sequence: "TGGCTGGGTTATCCGAAGTCTACGTCGCCG" },
                    { name: "sequence 13", sequence: "TTGACTTTGAATACGTCTCTGGGTCCG" },
                    { name: "sequence 14", sequence: "GAGGTGTTGGGTCAGAGGCGT" },
                    { name: "sequence 15", sequence: "ATGGCTGATCAGCGTACGTCGTTT" },
                ],
            },
            {
                label: "AAV9",
                value: "aav9",
                message: i18n.t("AAVMessage9"),
                data: [
                    { name: "sequence 1", sequence: "TCGGCCCAGCCGCATGAGGGGAGTAGTCGT" },
                    { name: "sequence 2", sequence: "AGTGACCAACCGATTCTGCTGGAGAAGGCT" },
                    { name: "sequence 3", sequence: "GGTGCCCAAAATCCCTCGTCGTTTAAGCTT" },
                    { name: "sequence 4", sequence: "TCGGATGGTACTGGTACTGGGTTGAGTCGT" },
                    { name: "sequence 5", sequence: "AGTGCCCAATGGAGTACGAATGCTGGTTAT" },
                    { name: "sequence 6", sequence: "AGTGCGCAAACTGGTTCCGTTACTGTTGTG" },
                    { name: "sequence 7", sequence: "GGTGCCCAAACTTATAATGTTCGTGGTCTG" },
                    { name: "sequence 8", sequence: "ATCAAAGACACCATTACTGTGCCTTTTAAG" },
                    { name: "sequence 9", sequence: "AGTGCCCGAACGCAGACTGCGGTTATCCGC" },
                    { name: "sequence 10", sequence: "GGTGCCCAAACTGTTCGAAATTCGGTTATT" },
                    { name: "sequence 11", sequence: "ATGTGCGGCACCCTTCAGCAGGTCTTTCGC" },
                    { name: "sequence 12", sequence: "AGTGACCGAACTTCCCTTAAGGTTAGTGTT" },
                    { name: "sequence 13", sequence: "GGTGCCCAAAATGGTGCTCCGTCGTCAGGT" },
                    { name: "sequence 14", sequence: "AGTGCCCAGAGTTGGGCTAGTACTGGTGCC" },
                    { name: "sequence 15", sequence: "AGTGATGGCACCACTATGACGCCGTCTGGT" },
                ],
            },
        ],
    };

    //* 响应式变量
    const refs = {
        data: ref<any[]>([]),
        context: ref<string>(""),
        sereotype: ref<string>("aav2"),
        option: ref<DiagramsOption[]>([
            { key: "id", name: "ID", width: 120 },
            { key: "score", name: i18n.t("PredictedViability"), width: 180 },
        ]),
    };

    //* 节点
    const nodes = {
        form: useTemplateRef<InstanceType<typeof ConsultForm>>("form"),
        confirm: ref<InstanceType<typeof ConfirmModal>>(),
    };

    //* 工具函数
    const utils = {
        //* 数据格式化
        format: () => {
            if (!refs.context.value) return [];
            else {
                const data: { name: string; sequence: string }[] = [];
                const config = { name: "", index: 0, merge: false, sequence: "" };
                const origin = refs.context.value?.split(/[\n\t\r]/).filter((v) => v) || [];
                origin.forEach((value, index) => {
                    //* 数据格式化
                    if (value.startsWith(">")) {
                        if (!config.merge) ((config.name = value.slice(1)), (config.merge = true));
                        else {
                            data.push({ name: config.name, sequence: config.sequence });
                            config.sequence = "";
                            config.merge = true;
                            config.name = value.slice(1);
                        }
                    } else {
                        value = value.toUpperCase().replace(/[^ATCG]/gi, "");
                        if (!value) return;
                        if (config.merge) config.sequence += value;
                        else {
                            data.push({ name: `sequence_${++config.index}`, sequence: value });
                        }
                    }

                    //* 检测是否存在最后一条数据
                    if (index + 1 == origin.length && config.name) {
                        data.push({ name: config.name, sequence: config.sequence });
                    }
                });

                return data;
            }
        },
    };

    //* 函数列表
    const methods = {
        //* 初始化
        init: () => {
            vars.observer?.disconnect();
            vars.observer = useResizeWObserver(document.body, () => {
                if (window.innerWidth > 1920) {
                    refs.option.value = [
                        { key: "id", name: "ID", width: 120 },
                        { key: "sequence", name: i18n.t("Sequence"), width: 240 },
                        { key: "score", name: i18n.t("PredictedViability"), width: 180 },
                    ];
                } else {
                    refs.option.value = [
                        { key: "id", name: "ID", width: 120 },
                        { key: "score", name: i18n.t("PredictedViability"), width: 180 },
                    ];
                }
            });
        },

        //* 测试数据
        demo: () => {
            //* 数据更新
            vars.data = vars.sereotype.find((item) => item.value === refs.sereotype.value)?.data || [];
            refs.context.value = vars.data.map((item) => `>${item.name}\n${item.sequence}`).join("\n");
        },

        //* 数据重置
        reset: () => {
            refs.data.value = [];
            refs.context.value = "";
        },

        //* 格式化函数
        format: () => {
            vars.data = utils.format();
            refs.context.value = vars.data.map((item) => `>${item.name}\n${item.sequence}`).join("\n");
        },

        //* 文件上传
        upload: () => {
            //* 初始化input节点
            const input = document.createElement("input");
            input.type = "file";
            input.accept = ".fasta, .fa";
            input.style.display = "none";
            input.onchange = async (ev) => {
                const file = (ev.target as HTMLInputElement)?.files?.[0];
                if (!file) return document.body.removeChild(input);
                else {
                    $loading.open({ message: i18n.t("UnderLoad") });
                    //* 文件读取
                    const reader = new FileReader();
                    reader.readAsText(file);
                    reader.onerror = () => (document.body.removeChild(input), $loading.close());
                    reader.onload = (ev) => {
                        //* 数据更新
                        refs.context.value = ev.target?.result as string;
                        vars.data = utils.format();
                        //* 删除input节点
                        document.body.removeChild(input);
                        //* 关闭加载器
                        $loading.close();
                    };
                }
            };

            //* 插入input节点
            document.body.appendChild(input);

            //* 触发文件上传
            input.click();
        },

        //* 表单提交
        submit: () => {
            //* 格式化数据
            vars.data = utils.format();
            if (!vars.data.length) return $message.error(i18n.t("Placeholder.InputFormatError"));
            if (localStorage.getItem("AAV_CAPSID_VIABILITY_PREDICTOR_FINGERPRINT") != get_canvas_fingerprint()) nodes.form.value?.open();
            else {
                //* 接口提交
                $loading.open({ message: i18n.t("UnderLoad") });
                api.frontend("/api/bio/aav", {
                    data: { sereotype: refs.sereotype.value, sequences: vars.data },
                    method: "POST",
                    success: (id) => ((vars.id = id), methods.get_tasks_status()),
                    error: () => $loading.close(),
                });
            }
        },

        //* 文件下载
        download: () => open(vars.download, "_blank"),
        //* 表单校验
        validator: () => {
            if (refs.context.value.startsWith(">")) return methods.submit();
            else {
                nodes.confirm.value?.open();
            }
        },

        //* 获取任务结果
        get_tasks_status: (delay?: number) => {
            vars.timeout && clearTimeout(vars.timeout);
            vars.timeout = setTimeout(() => {
                //* 接口控制器
                vars.controller?.abort("api cancel!!!");
                vars.controller = new AbortController();
                //* 接口请求
                api.frontend("/api/bio/aav/tasks", {
                    data: { id: vars.id },
                    signal: vars.controller.signal,
                    method: "POST",
                    finally: () => ($loading.close(), (vars.controller = undefined)),
                    success: (response) => {
                        switch (response.status) {
                            case "ERROR":
                                return $message.error(response.message || i18n.t("UnknownError"));
                            case "SUCCESS":
                                return ((refs.data.value = response.data || []), (vars.download = response.href || ""));
                            default:
                                return methods.get_tasks_status();
                        }
                    },
                });
            }, delay || 5000);
        },
    };

    //* 计算属性
    const computeds = {
        message: computed(() => {
            return vars.sereotype.find((item) => item.value === refs.sereotype.value)?.message || "";
        }),
    };

    return { vars, refs, nodes, methods, computeds };
};
